/**
 * The form's Repeat field (spec 2026-10-02 §7.2; mock artboards 1, 2, 6): an
 * input-styled button that names the rule and, when open, the preset chips —
 * Never · Daily · Weekly · Every 2 weeks · Monthly · Yearly · Custom… — plus
 * the Custom panel (interval stepper, unit chips, weekday circles or the
 * month-day hint). Stateless: the form owns the rule, `open` and `custom`, so
 * a re-seed resets everything at once.
 */
import React from 'react';
import { View, StyleSheet } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';
import { onColor } from '@/constants/shared/theme';
import { parseDateKey } from '@/utils/residents/todoCalendar';
import {
  REPEAT_PRESETS,
  dayOfKey,
  describeRepeat,
  matchPreset,
  ordinal,
  presetRule,
  weekdayOfKey,
} from '@/utils/residents/todoRecurrence';
import type { RepeatFrequency, RepeatRule } from '@/types/residents/todos';
import { ChoiceChip } from './ChoiceChip';

interface RepeatFieldProps {
  rule: RepeatRule | null;
  /** The date the rule hangs off: presets read its weekday / day / month. */
  startKey: string;
  open: boolean;
  /** The Custom panel is showing (even when the rule matches a preset). */
  custom: boolean;
  onToggleOpen: () => void;
  onChange: (rule: RepeatRule | null, custom: boolean) => void;
}

const UNITS: Array<{ frequency: RepeatFrequency; label: string }> = [
  { frequency: 'daily', label: 'Days' },
  { frequency: 'weekly', label: 'Weeks' },
  { frequency: 'monthly', label: 'Months' },
  { frequency: 'yearly', label: 'Years' },
];

const WEEKDAYS = [
  { day: 0, short: 'S', name: 'Sunday' },
  { day: 1, short: 'M', name: 'Monday' },
  { day: 2, short: 'T', name: 'Tuesday' },
  { day: 3, short: 'W', name: 'Wednesday' },
  { day: 4, short: 'T', name: 'Thursday' },
  { day: 5, short: 'F', name: 'Friday' },
  { day: 6, short: 'S', name: 'Saturday' },
];

const MAX_INTERVAL = 99;

/**
 * Weekday circle sizing. A 36pt circle with 4 above and below is a 44pt target. The seven share the
 * Custom panel's row on `space-between`, about (window - 76)pt wide (measured), and seven 36pt
 * circles need 252: below 328pt they touch and run past the panel's padding. Under COMPACT_WIDTH of
 * WINDOW width they are 32pt instead (224 in all, gaps of 3pt at 320) with 6 above and below: still
 * 44 tall. From COMPACT_WIDTH up they stay 36pt (gaps of 2pt at 340, 5pt at 360).
 *
 * Sideways, 2 a side in both: the most that keeps neighbouring targets apart on a 360pt phone (4
 * would meet below ~375pt). Under ~352pt (36pt circles) or ~324pt (32pt) the gaps shrink below 4pt
 * and the 2s meet by up to 2pt, inside the gap, where no circle is drawn.
 */
const COMPACT_WIDTH = 340;
const DAY_HIT_SLOP = { top: 4, bottom: 4, left: 2, right: 2 };
const DAY_HIT_SLOP_COMPACT = { top: 6, bottom: 6, left: 2, right: 2 };

/** The rule a unit chip switches to: same interval and end date; parts the unit needs come from the start. */
export function ruleForUnit(
  frequency: RepeatFrequency,
  rule: RepeatRule,
  startKey: string,
): RepeatRule {
  return {
    frequency,
    interval: rule.interval,
    weekdays: frequency === 'weekly' ? (rule.weekdays ?? [weekdayOfKey(startKey)]) : null,
    monthDay:
      frequency === 'monthly' || frequency === 'yearly' ? (rule.monthDay ?? dayOfKey(startKey)) : null,
    until: rule.until,
  };
}

/**
 * The Custom panel's note on a monthly / yearly rule's day. A short month clamps a day past its
 * length to its last day. A monthly rule meets every month, so February's 28 is the shortest it
 * meets (the note starts at the 29th). A yearly rule keeps ONE month, the start date's, so it only
 * clamps when its day is past THAT month's length, February counting 28: a Feb 29 repeat lands on
 * the 28th in a year without one. The yearly note names its month, as the field's summary does.
 */
function monthDayHint(rule: RepeatRule, monthDay: number, startKey: string): string {
  const start = parseDateKey(startKey);
  const yearly = rule.frequency === 'yearly';
  // Day 0 of the following month, in 2001 (not a leap year), is this month's length.
  const shortest = yearly ? new Date(2001, start.getMonth() + 1, 0).getDate() : 28;
  const on = yearly
    ? `${start.toLocaleDateString('en-US', { month: 'short' })} ${monthDay}`
    : `the ${ordinal(monthDay)}`;
  return `On ${on}, from the start date.${
    monthDay > shortest ? ' In shorter months it lands on the last day.' : ''
  }`;
}

export const RepeatField: React.FC<RepeatFieldProps> = ({
  rule,
  startKey,
  open,
  custom,
  onToggleOpen,
  onChange,
}) => {
  const type = useTypography();
  const space = useSpacing();
  const radius = useRadius();
  // The WINDOW width, as UnifiedModal's own layout switch reads it. Read here and applied below, not
  // inside the themed styles: those are memoized on the theme mode and would not follow a rotation.
  const { width } = useResponsive();
  const compact = width < COMPACT_WIDTH;
  const styles = useThemedStyles(({ colors, semantic }) =>
    StyleSheet.create({
      group: {
        gap: space.sm,
      },
      repeatField: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: space.sm,
        backgroundColor: semantic.background.input,
        borderColor: semantic.border.input,
        borderWidth: 1,
        borderRadius: radius.md,
        paddingHorizontal: space.md,
        paddingVertical: space.sm,
      },
      summary: {
        ...type.body,
        color: colors.text.primary,
        flex: 1,
      },
      icon: {
        color: colors.text.muted,
      },
      chips: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: space.sm,
      },
      panel: {
        gap: space.md,
        padding: space.md,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: colors.card.border,
      },
      everyRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: space.md,
      },
      everyText: {
        ...type.body,
        color: colors.text.primary,
      },
      stepper: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: semantic.border.input,
        borderRadius: radius.md,
        backgroundColor: semantic.background.input,
      },
      stepTarget: {
        minWidth: 36,
        minHeight: 36,
        alignItems: 'center',
        justifyContent: 'center',
      },
      stepIcon: {
        color: colors.text.primary,
      },
      stepValue: {
        ...type.cardTitle,
        color: colors.text.primary,
        minWidth: 28,
        textAlign: 'center',
      },
      weekdays: {
        flexDirection: 'row',
        justifyContent: 'space-between',
      },
      day: {
        minWidth: 36,
        minHeight: 36,
        borderRadius: radius.full,
        borderWidth: 1,
        borderColor: colors.card.border,
        alignItems: 'center',
        justifyContent: 'center',
      },
      // Under COMPACT_WIDTH (see there): the same circle, 32pt.
      dayCompact: {
        minWidth: 32,
        minHeight: 32,
      },
      daySelected: {
        backgroundColor: semantic.interactive.primary,
        borderColor: semantic.interactive.primary,
      },
      dayText: {
        ...type.buttonSmall,
        color: semantic.text.secondary,
      },
      dayTextSelected: {
        color: onColor(semantic.interactive.primary),
      },
      hint: {
        ...type.caption,
        color: semantic.text.secondary,
      },
    }),
  );

  const summary = rule ? describeRepeat(rule, startKey) : 'Never';
  const preset = rule ? matchPreset(rule, startKey) : null;
  const showCustom = rule !== null && (custom || preset === 'custom');

  const changeInterval = (interval: number) => {
    if (rule) onChange({ ...rule, interval: Math.min(MAX_INTERVAL, Math.max(1, interval)) }, true);
  };
  const toggleWeekday = (day: number) => {
    if (!rule?.weekdays) return;
    const on = rule.weekdays.includes(day);
    if (on && rule.weekdays.length === 1) return; // a weekly repeat keeps at least one day
    const weekdays = on
      ? rule.weekdays.filter(d => d !== day)
      : [...rule.weekdays, day].sort((a, b) => a - b);
    onChange({ ...rule, weekdays }, true);
  };

  return (
    <View style={styles.group}>
      <Hoverable
        hover="field"
        style={styles.repeatField}
        onPress={onToggleOpen}
        accessibilityRole="button"
        accessibilityLabel={`Repeat: ${summary}`}
        accessibilityState={{ expanded: open }}
      >
        <Icon name="repeat" size={16} style={styles.icon} />
        <Text style={styles.summary}>{summary}</Text>
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} style={styles.icon} />
      </Hoverable>

      {open ? (
        <View style={styles.chips}>
          <ChoiceChip label="Never" selected={rule === null} onPress={() => onChange(null, false)} />
          {REPEAT_PRESETS.map(p => (
            <ChoiceChip
              key={p.preset}
              label={p.label}
              selected={!showCustom && preset === p.preset}
              onPress={() => onChange(presetRule(p.preset, startKey, rule?.until ?? null), false)}
            />
          ))}
          <ChoiceChip
            label="Custom…"
            accessibilityLabel="Custom repeat"
            selected={showCustom}
            onPress={() => onChange(rule ?? presetRule('weekly', startKey), true)}
          />
        </View>
      ) : null}

      {open && showCustom && rule ? (
        <View style={styles.panel}>
          <View style={styles.everyRow}>
            <Text style={styles.everyText}>Every</Text>
            <View
              style={styles.stepper}
              accessible
              accessibilityRole="adjustable"
              accessibilityLabel="Repeat interval"
              // `text` too: with only min / max / now, iOS reads the value as a percentage.
              accessibilityValue={{
                min: 1,
                max: MAX_INTERVAL,
                now: rule.interval,
                text: String(rule.interval),
              }}
              accessibilityActions={[{ name: 'increment' }, { name: 'decrement' }]}
              onAccessibilityAction={e =>
                changeInterval(rule.interval + (e.nativeEvent.actionName === 'increment' ? 1 : -1))
              }
            >
              <Hoverable
                hover="glass"
                style={styles.stepTarget}
                onPress={() => changeInterval(rule.interval - 1)}
                disabled={rule.interval <= 1}
                // 36pt button + 4 a side = the 44pt target; the value between the two buttons is
                // 28pt wide at least, so their targets never meet.
                hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
                accessibilityRole="button"
                accessibilityLabel="Decrease interval"
              >
                <Icon name="remove" size={16} style={styles.stepIcon} />
              </Hoverable>
              <Text style={styles.stepValue}>{rule.interval}</Text>
              <Hoverable
                hover="glass"
                style={styles.stepTarget}
                onPress={() => changeInterval(rule.interval + 1)}
                disabled={rule.interval >= MAX_INTERVAL}
                hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
                accessibilityRole="button"
                accessibilityLabel="Increase interval"
              >
                <Icon name="add" size={16} style={styles.stepIcon} />
              </Hoverable>
            </View>
          </View>

          <View style={styles.chips}>
            {UNITS.map(unit => (
              <ChoiceChip
                key={unit.frequency}
                label={unit.label}
                selected={rule.frequency === unit.frequency}
                onPress={() => onChange(ruleForUnit(unit.frequency, rule, startKey), true)}
              />
            ))}
          </View>

          {rule.frequency === 'weekly' && rule.weekdays ? (
            <View style={styles.weekdays}>
              {WEEKDAYS.map(w => {
                const on = rule.weekdays?.includes(w.day) ?? false;
                return (
                  <Hoverable
                    key={w.day}
                    hover={on ? 'none' : 'glass'}
                    style={[styles.day, compact && styles.dayCompact, on && styles.daySelected]}
                    onPress={() => toggleWeekday(w.day)}
                    // 44pt tall either way; see COMPACT_WIDTH for the sizes and the 2pt sideways slop.
                    hitSlop={compact ? DAY_HIT_SLOP_COMPACT : DAY_HIT_SLOP}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: on }}
                    accessibilityLabel={w.name}
                  >
                    <Text style={[styles.dayText, on && styles.dayTextSelected]}>{w.short}</Text>
                  </Hoverable>
                );
              })}
            </View>
          ) : null}

          {(rule.frequency === 'monthly' || rule.frequency === 'yearly') && rule.monthDay ? (
            <Text style={styles.hint} scale="content">
              {monthDayHint(rule, rule.monthDay, startKey)}
            </Text>
          ) : null}
        </View>
      ) : null}
    </View>
  );
};

RepeatField.displayName = 'RepeatField';
